import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import {
  getAdminPropertyById,
  postPropertyToFacebook,
  blockProperty,
  unblockProperty,
  publishProperty,
  unpublishProperty,
  blockUnit,
  unblockUnit,
  publishUnit,
  unpublishUnit,
  deleteAdminProperty,
  deleteAdminUnit,
} from '../../../api/services/adminService';
import './PropertyDetails.css';

const formatScalar = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatYesNo = (value) => {
  if (value === null || value === undefined) return '—';
  return value ? 'Yes' : 'No';
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `KES ${num.toLocaleString('en-KE', { maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const humanizeCamelKey = (key) => {
  if (!key) return '';
  const spaced = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ');
  const trimmed = spaced.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const PAYMENT_FIELD_LABELS = {
  method: 'Payment method',
  tillNumber: 'Till number',
  accountName: 'Account name',
  paybillNumber: 'Paybill number',
  paybillAccount: 'Paybill account',
  acceptsPochi: 'Accepts Pochi la Biashara',
  acceptsSendMoney: 'Accepts send money (M-Pesa)',
};

const formatPaymentMethod = (method) => {
  if (method === null || method === undefined || method === '') return '—';
  const m = String(method).toUpperCase();
  const map = { TILL: 'Till', PAYBILL: 'Paybill', MPESA: 'M-Pesa', POCHI: 'Pochi la Biashara' };
  return map[m] || humanizeCamelKey(String(method).replace(/_/g, ' '));
};

const formatPaymentValue = (key, val) => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return formatYesNo(val);
  if (key === 'method') return formatPaymentMethod(val);
  return String(val);
};

const formatAmenityLabel = (raw) => {
  if (raw === null || raw === undefined) return '';
  return String(raw)
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((w) => {
      if (!w) return '';
      const lower = w.toLowerCase();
      if (lower === 'wifi') return 'Wi-Fi';
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ')
    .trim();
};

const StatusBadges = ({ active, published, size }) => (
  <div className="pd-badges" data-size={size || 'md'}>
    <span className={`pd-badge ${active ? 'active' : 'inactive'}`}>{active ? 'Active' : 'Blocked'}</span>
    <span className={`pd-badge ${published ? 'published' : 'unpublished'}`}>
      {published ? 'Published' : 'Unpublished'}
    </span>
  </div>
);

const InfoRow = ({ label, children }) => (
  <div className="pd-info-item">
    <span className="pd-info-label">{label}</span>
    <span className="pd-info-value">{children}</span>
  </div>
);

const Section = ({ title, action, children }) => (
  <section className="pd-section">
    <div className="pd-section-head">
      <h2>{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

/** Block/unblock + publish/unpublish (+ optional delete) action buttons. */
const ActionBar = ({ isActive, isPublished, busyAction, onAction, withDelete }) => (
  <div className="pd-action-bar">
    {isActive ? (
      <button type="button" className="btn btn-danger-soft btn-sm" onClick={() => onAction('block')} disabled={!!busyAction}>
        {busyAction === 'block' ? '…' : 'Block'}
      </button>
    ) : (
      <button type="button" className="btn btn-success-soft btn-sm" onClick={() => onAction('unblock')} disabled={!!busyAction}>
        {busyAction === 'unblock' ? '…' : 'Unblock'}
      </button>
    )}
    {isPublished ? (
      <button type="button" className="btn btn-secondary-soft btn-sm" onClick={() => onAction('unpublish')} disabled={!!busyAction}>
        {busyAction === 'unpublish' ? '…' : 'Unpublish'}
      </button>
    ) : (
      <button type="button" className="btn btn-success-soft btn-sm" onClick={() => onAction('publish')} disabled={!!busyAction}>
        {busyAction === 'publish' ? '…' : 'Publish'}
      </button>
    )}
    {withDelete ? (
      <button type="button" className="btn btn-danger-strong btn-sm" onClick={() => onAction('delete')} disabled={!!busyAction}>
        {busyAction === 'delete' ? '…' : 'Delete'}
      </button>
    ) : null}
  </div>
);

const UnitCard = ({ unit, busyAction, onAction }) => {
  const rent = unit.monthlyRent != null ? `${formatMoney(unit.monthlyRent)}/mo` : null;
  const nightly = unit.pricePerNight != null ? `${formatMoney(unit.pricePerNight)}/night` : null;
  return (
    <div className="pd-unit-card">
      <div className="pd-unit-head">
        <div className="pd-unit-title">
          <span className="pd-unit-number">{unit.unitNumber || unit.name || 'Unit'}</span>
          {unit.unitTypeName ? <span className="pd-unit-type">{unit.unitTypeName}</span> : null}
        </div>
        <StatusBadges active={unit.isActive} published={unit.isPublic} size="sm" />
      </div>
      <div className="pd-unit-facts">
        <span className={`pd-chip ${unit.isOccupied ? 'occupied' : 'vacant'}`}>
          {unit.isOccupied ? 'Occupied' : 'Vacant'}
        </span>
        {unit.bedrooms != null ? <span className="pd-chip">{unit.bedrooms} bd</span> : null}
        {unit.bathrooms != null ? <span className="pd-chip">{unit.bathrooms} ba</span> : null}
        {rent ? <span className="pd-chip money">{rent}</span> : null}
        {nightly ? <span className="pd-chip money">{nightly}</span> : null}
        {unit.depositAmount != null ? <span className="pd-chip">Dep {formatMoney(unit.depositAmount)}</span> : null}
      </div>
      <ActionBar
        isActive={unit.isActive}
        isPublished={unit.isPublic}
        busyAction={busyAction}
        onAction={onAction}
        withDelete
      />
    </div>
  );
};

const PropertyDetails = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroBlob, setHeroBlob] = useState(null);
  const [heroStatus, setHeroStatus] = useState('idle');
  const [galleryBlobs, setGalleryBlobs] = useState([]);
  const [agreementBusy, setAgreementBusy] = useState(false);
  const [propBusy, setPropBusy] = useState(null);
  const [fbBusy, setFbBusy] = useState(false);
  const [unitBusy, setUnitBusy] = useState({});
  const [actionError, setActionError] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminPropertyById(propertyId);
      const full = res?.data || res;
      setProperty(full && typeof full === 'object' ? full : null);
    } catch (e) {
      setError(e?.message || 'Failed to load property');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const { heroCandidate, extraGallery } = useMemo(() => {
    if (!property) return { heroCandidate: null, extraGallery: [] };
    const galleryUrls = Array.isArray(property.imageUrls)
      ? [...new Set(property.imageUrls.filter(Boolean))]
      : [];
    const hero = property.imageUrl || galleryUrls[0] || null;
    const extra = galleryUrls.length > 0 ? galleryUrls.filter((u) => u !== hero) : [];
    return { heroCandidate: hero, extraGallery: extra };
  }, [property]);

  useEffect(() => {
    let cancelled = false;
    const resolveMediaUrl = async (url) => {
      if (!url) return null;
      if (url.startsWith('blob:') || url.startsWith('data:')) return url;
      if (!url.startsWith('http')) return url;
      return fetchAuthenticatedImage(url);
    };

    setHeroBlob(null);
    setGalleryBlobs([]);
    setHeroStatus(heroCandidate ? 'loading' : 'idle');

    (async () => {
      if (heroCandidate) {
        const blob = await resolveMediaUrl(heroCandidate);
        if (cancelled) return;
        if (blob) {
          setHeroBlob(blob);
          setHeroStatus('ok');
        } else {
          setHeroStatus('error');
        }
      }
      const results = [];
      for (let i = 0; i < extraGallery.length; i++) {
        const b = await resolveMediaUrl(extraGallery[i]);
        if (cancelled) return;
        if (b) results.push({ blobUrl: b, index: i });
      }
      if (!cancelled) setGalleryBlobs(results);
    })();

    return () => {
      cancelled = true;
    };
  }, [heroCandidate, extraGallery]);

  const openAgreementAuthenticated = async () => {
    const u = property?.agreementUrl;
    if (!u) return;
    setAgreementBusy(true);
    try {
      let blobUrl = u;
      if (u.startsWith('http')) blobUrl = await fetchAuthenticatedImage(u);
      if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setAgreementBusy(false);
    }
  };

  const runPropertyAction = async (action) => {
    setActionError(null);
    setActionNotice(null);
    if (action === 'delete') {
      const ok = window.confirm(
        `Delete "${property?.name || 'this property'}" and all its units? This cannot be undone.`
      );
      if (!ok) return;
    }
    setPropBusy(action);
    try {
      if (action === 'block') await blockProperty(propertyId);
      else if (action === 'unblock') await unblockProperty(propertyId);
      else if (action === 'publish') await publishProperty(propertyId);
      else if (action === 'unpublish') await unpublishProperty(propertyId);
      else if (action === 'delete') {
        await deleteAdminProperty(propertyId);
        navigate('/system-admin/properties');
        return;
      }
      await load();
      setActionNotice(`Property ${action}${action.endsWith('e') ? 'd' : 'ed'}`);
    } catch (e) {
      setActionError(e?.message || `Failed to ${action} property`);
    } finally {
      setPropBusy(null);
    }
  };

  const postToFacebook = async () => {
    setActionError(null);
    setActionNotice(null);
    setFbBusy(true);
    try {
      await postPropertyToFacebook(propertyId);
      await load();
      setActionNotice('Property posted to Facebook');
    } catch (e) {
      setActionError(e?.message || 'Failed to post to Facebook');
    } finally {
      setFbBusy(false);
    }
  };

  const runUnitAction = async (unit, action) => {
    setActionError(null);
    setActionNotice(null);
    if (action === 'delete') {
      const ok = window.confirm(`Delete unit "${unit.unitNumber || unit.name || ''}"? This cannot be undone.`);
      if (!ok) return;
    }
    setUnitBusy((prev) => ({ ...prev, [unit.id]: action }));
    try {
      if (action === 'block') await blockUnit(propertyId, unit.id);
      else if (action === 'unblock') await unblockUnit(propertyId, unit.id);
      else if (action === 'publish') await publishUnit(propertyId, unit.id);
      else if (action === 'unpublish') await unpublishUnit(propertyId, unit.id);
      else if (action === 'delete') await deleteAdminUnit(propertyId, unit.id);
      await load();
    } catch (e) {
      setActionError(e?.message || `Failed to ${action} unit`);
    } finally {
      setUnitBusy((prev) => {
        const next = { ...prev };
        delete next[unit.id];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="pd-container">
        <button type="button" className="pd-back" onClick={() => navigate('/system-admin/properties')}>
          <FaArrowLeft /> Back to properties
        </button>
        <p className="pd-state">Loading property…</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="pd-container">
        <button type="button" className="pd-back" onClick={() => navigate('/system-admin/properties')}>
          <FaArrowLeft /> Back to properties
        </button>
        <p className="pd-state pd-state-error">{error || 'Property not found.'}</p>
      </div>
    );
  }

  const loc = property.location || {};
  const contacts = property.contacts || {};
  const payment = property.paymentDetails && typeof property.paymentDetails === 'object' ? property.paymentDetails : {};
  const paymentEntries = Object.entries(payment);
  const units = Array.isArray(property.units) ? property.units : [];

  return (
    <div className="pd-container">
      <button type="button" className="pd-back" onClick={() => navigate('/system-admin/properties')}>
        <FaArrowLeft /> Back to properties
      </button>

      {/* Header banner */}
      <div className="pd-header">
        <div className="pd-header-media">
          {heroStatus === 'ok' && heroBlob ? (
            <img src={heroBlob} alt="" className="pd-header-img" />
          ) : (
            <div className="pd-header-placeholder">
              {heroStatus === 'loading' ? 'Loading photo…' : 'No photo'}
            </div>
          )}
        </div>
        <div className="pd-header-body">
          <div className="pd-header-title">
            <h1>{property.name || 'Property'}</h1>
            <StatusBadges active={property.isActive} published={property.published} />
          </div>
          {property.address ? <p className="pd-header-sub">{property.address}</p> : null}
          <div className="pd-header-actions">
            <ActionBar
              isActive={property.isActive}
              isPublished={property.published}
              busyAction={propBusy}
              onAction={runPropertyAction}
              withDelete
            />
            <button type="button" className="btn btn-secondary btn-sm" onClick={postToFacebook} disabled={fbBusy}>
              {fbBusy ? 'Posting…' : 'Post to Facebook'}
            </button>
          </div>
        </div>
      </div>

      {actionError ? <div className="pd-alert error" role="alert">{actionError}</div> : null}
      {actionNotice ? <div className="pd-alert success" role="status">{actionNotice}</div> : null}

      {/* Stats strip */}
      <div className="pd-stats">
        <div className="pd-stat">
          <span className="pd-stat-value">{formatScalar(property.totalUnits || property.unitCount)}</span>
          <span className="pd-stat-label">Total units</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{formatScalar(property.occupiedUnits)}</span>
          <span className="pd-stat-label">Occupied</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{formatScalar(property.vacantUnits)}</span>
          <span className="pd-stat-label">Vacant</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{formatScalar(property.publicUnits)}</span>
          <span className="pd-stat-label">Public units</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{formatMoney(property.totalMonthlyRentExpected)}</span>
          <span className="pd-stat-label">Expected rent</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">
            {property.avgRating != null ? `${property.avgRating} (${property.ratingCount ?? 0})` : '—'}
          </span>
          <span className="pd-stat-label">Avg rating</span>
        </div>
      </div>

      <div className="pd-columns">
        <div className="pd-col-main">
          <Section title={`Units${units.length ? ` (${units.length})` : ''}`}>
            {units.length === 0 ? (
              <p className="pd-muted">No units on this property.</p>
            ) : (
              <div className="pd-units-grid">
                {units.map((unit) => (
                  <UnitCard
                    key={unit.id}
                    unit={unit}
                    busyAction={unitBusy[unit.id]}
                    onAction={(action) => runUnitAction(unit, action)}
                  />
                ))}
              </div>
            )}
          </Section>

          <Section title="Overview">
            {property.description ? (
              <p className="pd-overview">{property.description}</p>
            ) : (
              <p className="pd-muted">No description provided.</p>
            )}
          </Section>

          {extraGallery.length > 0 ? (
            <Section title="Photo gallery">
              {galleryBlobs.length > 0 ? (
                <div className="pd-gallery">
                  {galleryBlobs.map(({ blobUrl, index }) => (
                    <a key={`${blobUrl}-${index}`} href={blobUrl} target="_blank" rel="noopener noreferrer" className="pd-gallery-item">
                      <img src={blobUrl} alt={`Gallery item ${index + 1}`} />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="pd-muted">Loading gallery…</p>
              )}
            </Section>
          ) : null}

          <Section title="Amenities">
            {Array.isArray(property.amenities) && property.amenities.length > 0 ? (
              <div className="pd-amenities">
                {property.amenities.map((a, index) => (
                  <div key={`${a}-${index}`} className="pd-amenity">
                    <span className="pd-amenity-check">✓</span>
                    <span>{formatAmenityLabel(a)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="pd-muted">None listed.</p>
            )}
          </Section>
        </div>

        <div className="pd-col-side">
          <Section title="Location">
            <div className="pd-info-list">
              <InfoRow label="Area">{formatScalar(loc.area)}</InfoRow>
              <InfoRow label="City / region">{formatScalar(loc.city)}</InfoRow>
              <InfoRow label="Coordinates">
                {loc.lat != null && loc.lng != null ? `${loc.lat}, ${loc.lng}` : '—'}
              </InfoRow>
            </div>
          </Section>

          <Section title="Contact">
            <div className="pd-info-list">
              <InfoRow label="Email">
                {contacts.email ? <a href={`mailto:${contacts.email}`}>{contacts.email}</a> : '—'}
              </InfoRow>
              <InfoRow label="Phone">{formatScalar(contacts.phone)}</InfoRow>
              <InfoRow label="Caretaker phone">{formatScalar(contacts.caretakerPhone)}</InfoRow>
            </div>
          </Section>

          {paymentEntries.length > 0 ? (
            <Section title="Payment details">
              <div className="pd-info-list">
                {paymentEntries.map(([key, val]) => (
                  <InfoRow key={key} label={PAYMENT_FIELD_LABELS[key] || humanizeCamelKey(key)}>
                    {formatPaymentValue(key, val)}
                  </InfoRow>
                ))}
              </div>
            </Section>
          ) : null}

          <Section title="Meta">
            <div className="pd-info-list">
              <InfoRow label="Created">{formatDate(property.createdAt)}</InfoRow>
              <InfoRow label="Updated">{formatDate(property.updatedAt)}</InfoRow>
              <InfoRow label="Facebook">
                {property.facebookPostId ? `Posted ${formatDate(property.facebookPostedAt)}` : 'Not posted'}
              </InfoRow>
            </div>
            {property.agreementUrl ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm pd-agreement-btn"
                onClick={openAgreementAuthenticated}
                disabled={agreementBusy}
              >
                {agreementBusy ? 'Opening…' : 'View agreement document'}
              </button>
            ) : null}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
